// src/components/tag/TagCreationForm.tsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
    createTag,
    getTagById,
    updateTag,
} from "../../../../services/tagServices/TagService";
import { useAuth } from "../../../../firebase/AuthProvider";
import Tag from "../../../../interfaces/tag/Tag";
import { Button } from "react-bootstrap";
import backarrowbtn from '../../../../assets/images/backarrowbtn.svg';
import { toast } from "react-toastify";
import { ApiResponse } from "../../../../interfaces/genericResponse/GenericResponse";

interface TagCreationFormProps {
    isEdit?: boolean; // if true, this form is editing an existing tag
}

const TagCreationForm: React.FC<TagCreationFormProps> = ({
    isEdit = false,
}) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { currentUser } = useAuth();

    const [tagData, setTagData] = useState<Omit<Tag, "id">>({
        name: "",
        color: "",
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // If editing, fetch the existing tag details
    useEffect(() => {
        if (isEdit && id && currentUser) {
            (async () => {
                setLoading(true);
                const authToken = await currentUser.getIdToken();
                const existingTag = await getTagById(authToken, parseInt(id));
                setLoading(false);
                if (!existingTag) {
                    setError("Tag not found or error fetching.");
                    return;
                }
                // Populate the form with existing data
                setTagData({
                    name: existingTag.name,
                    color: existingTag.color ?? "",
                });
            })();
        }
    }, [isEdit, id, currentUser]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setTagData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        if (!currentUser) return;
        setLoading(true);
        const authToken = await currentUser.getIdToken();

        try {
            let res : ApiResponse<Tag | null>;
            if (isEdit && id) {
                // Update existing
                res = await updateTag(authToken, parseInt(id), tagData);
            } else {
                // Create new
                res = await createTag(authToken, tagData);
            }
            
            if (res.success) {
                toast.success(res.message);
                navigate("/tags");
            } else {
                toast.error(res.message)
            }

        } catch (err: any) {
            console.error("Error in saving tag:", err);
            setError(err.toString());
            setLoading(false);
        } finally {
            setLoading(false);


        }
    };

    return (
        <>
            <div className="new_activity_container">
                <div className="common_card">
                    <div className="top border_tittle top_bar_flx justify-content-start align-items-start">
                        <Link to={'/dashboard/owner/tags'}><img style={{ width: '1.5rem' }} src={backarrowbtn} alt="backarrowbtn" /></Link>
                        <div className="top_bar_tittle">
                            <h2> {isEdit ? "Edit Tag" : "Create New Tag"}</h2>
                            {error && <p>{error}</p>}
                        </div>
                    </div>
                    {loading ? (
                        <p>Loading...</p>
                    ) : (
                        <>
                            <div className="custom_input_flled">
                                <label>Tag Name</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={tagData.name}
                                    placeholder="Example Tag Name"
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="custom_input_flled mb-0">
                                <label>Color (Hex or any string)</label>
                                <input
                                    type="text"
                                    name="color"
                                    value={tagData.color ?? ""}
                                    onChange={handleChange}
                                    placeholder="#FF5733 or leave blank"
                                />
                            </div>
                            <div className="form_btn_flx mby_t_24">
                                <Button onClick={handleSubmit}>{isEdit ? "Update Tag" : "Create Tag"}</Button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default TagCreationForm;
